/**
 * Administrator dashboard page
 * PURPOSE: Manages listings, moderation, analytics, gallery images and admin accounts.
 *
 * EDITING GUIDE:
 * - Read the comments above each section or function before changing behavior.
 * - Keep validation, authentication and error handling in place for production safety.
 * - Search for "EDIT HERE" to find the most common business-content settings.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { api, assetUrl } from '../api';
import { PropertyMapPicker } from '../components/PropertyMap';
import './AdminDashboard.css';

// PROPERTY FORM DEFAULTS: used by both create and edit flows.
const emptyPropertyForm = {
    title: '',
    price: '',
    location: '',
    // MAP POSITION: saved with the property and displayed on its public details page.
    latitude: '',
    longitude: '',
    type: 'Vente',
    category: 'Appartement',
    bedrooms: '',
    bathrooms: '',
    area: '',
    description: '',
    status: 'disponible',
    contactPhone: ''
};

// ADMIN ACCOUNT FORM DEFAULTS: owner accounts can create editor or owner users.
const emptyAccountForm = {
    username: '',
    displayName: '',
    password: '',
    role: 'editor'
};

// CUSTOM FIELD OPTIONS: these correspond to the types accepted by the backend controller.
const CUSTOM_FIELD_TYPES = [
    { value: 'text', label: 'Texte' },
    { value: 'number', label: 'Nombre' },
    { value: 'date', label: 'Date' },
    { value: 'boolean', label: 'Oui / Non' }
];
const MAX_CUSTOM_FIELDS = 20;
let customFieldSequence = 0;

/**
 * Create one editable custom-field row with a stable browser-only identifier.
 * The clientId is used only by React and is never stored in MongoDB.
 */
function makeCustomField(field = {}) {
    customFieldSequence += 1;
    return {
        clientId: field.clientId || `custom-field-${Date.now()}-${customFieldSequence}`,
        label: field.label || '',
        value: field.value ?? '',
        fieldType: field.fieldType || 'text'
    };
}

/**
 * Render the reusable custom-field builder used by both create and edit property forms.
 */
function CustomFieldsEditor({ fields, onChange }) {
    const addField = () => {
        if (fields.length >= MAX_CUSTOM_FIELDS) return;
        onChange([...fields, makeCustomField()]);
    };

    const updateField = (clientId, key, value) => {
        onChange(fields.map((field) => (
            field.clientId === clientId ? { ...field, [key]: value } : field
        )));
    };

    const removeField = (clientId) => {
        onChange(fields.filter((field) => field.clientId !== clientId));
    };

    return (
        <section className="custom-fields-builder form-field-full">
            <div className="custom-fields-heading">
                <div>
                    <h3>Champs personnalisés</h3>
                    <p>Ajoutez des informations propres à ce bien, par exemple « Étage », « Référence » ou « Année de construction ».</p>
                </div>
                <button type="button" className="add-custom-field-btn" onClick={addField} disabled={fields.length >= MAX_CUSTOM_FIELDS}>
                    + Ajouter un champ
                </button>
            </div>

            {fields.length === 0 ? (
                <p className="custom-fields-empty">Aucun champ personnalisé. Cliquez sur « Ajouter un champ » pour en créer un.</p>
            ) : (
                <div className="custom-fields-list">
                    {fields.map((field, index) => (
                        <div className="custom-field-row" key={field.clientId}>
                            <label>
                                <span>Nom du champ</span>
                                <input
                                    type="text"
                                    maxLength="80"
                                    placeholder="Ex. Étage"
                                    value={field.label}
                                    onChange={(event) => updateField(field.clientId, 'label', event.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                <span>Type</span>
                                <select
                                    value={field.fieldType}
                                    onChange={(event) => updateField(field.clientId, 'fieldType', event.target.value)}
                                >
                                    {CUSTOM_FIELD_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                            </label>
                            <label className="custom-field-value">
                                <span>Valeur</span>
                                {field.fieldType === 'boolean' ? (
                                    <select value={field.value} onChange={(event) => updateField(field.clientId, 'value', event.target.value)} required>
                                        <option value="">Choisir...</option>
                                        <option value="true">Oui</option>
                                        <option value="false">Non</option>
                                    </select>
                                ) : (
                                    <input
                                        type={field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'}
                                        step={field.fieldType === 'number' ? 'any' : undefined}
                                        maxLength={field.fieldType === 'text' ? 500 : undefined}
                                        placeholder={field.fieldType === 'text' ? 'Valeur à afficher' : undefined}
                                        value={field.value}
                                        onChange={(event) => updateField(field.clientId, 'value', event.target.value)}
                                        required
                                    />
                                )}
                            </label>
                            <button type="button" className="remove-custom-field-btn" onClick={() => removeField(field.clientId)} aria-label={`Supprimer le champ ${index + 1}`}>
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <small>{fields.length} / {MAX_CUSTOM_FIELDS} champ(s)</small>
        </section>
    );
}

/**
 * Render a compact five-star score inside the review moderation cards.
 */
function AdminStars({ rating = 0 }) {
    return (
        <span className="admin-review-stars" aria-label={`${rating} étoiles sur 5`}>
            {[1, 2, 3, 4, 5].map((star) => (
                <span key={star} className={star <= Number(rating) ? 'filled' : ''}>★</span>
            ))}
        </span>
    );
}

/**
 * Manage administrator authentication, properties, review moderation, analytics and accounts.
 */
const AdminDashboard = () => {
    // COMPONENT STATE: authentication, properties, accounts, modal editing, analytics and moderation.
    const [properties, setProperties] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [accountLoading, setAccountLoading] = useState(false);
    const [authChecking, setAuthChecking] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [currentAdmin, setCurrentAdmin] = useState(null);
    const [authError, setAuthError] = useState('');
    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const [formData, setFormData] = useState(emptyPropertyForm);
    // CREATE CUSTOM FIELDS: extra labelled values saved only with the new property.
    const [customFields, setCustomFields] = useState([]);
    const [accountForm, setAccountForm] = useState(emptyAccountForm);
    const [files, setFiles] = useState([]);
    const [editingProperty, setEditingProperty] = useState(null);
    const [editFormData, setEditFormData] = useState(emptyPropertyForm);
    // EDIT CUSTOM FIELDS: a separate copy prevents accidental changes before Save is clicked.
    const [editCustomFields, setEditCustomFields] = useState([]);
    const [editImageReplacements, setEditImageReplacements] = useState({});
    const [editPreviewUrls, setEditPreviewUrls] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [moderationLoadingId, setModerationLoadingId] = useState(null);

    // REVIEW MODERATION STATE: list, selected filter and current action loading state.
    const [reviews, setReviews] = useState([]);
    const [reviewCounts, setReviewCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
    const [reviewStatusFilter, setReviewStatusFilter] = useState('all');
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [reviewModerationLoadingId, setReviewModerationLoadingId] = useState(null);

    /**
     * Clear protected dashboard state and return the interface to the login screen.
     */
    const forceLogin = () => {
        setAuthenticated(false);
        setCurrentAdmin(null);
        setProperties([]);
        setAdminUsers([]);
        setAnalytics(null);
        setReviews([]);
        setReviewCounts({ total: 0, pending: 0, approved: 0, rejected: 0 });
    };

    /**
     * Load properties from the backend and update the page state.
     */
    const fetchProperties = useCallback(async () => {
        const response = await api.get('/api/properties/admin/all');
        setProperties(response.data.data || []);
    }, []);

    /**
     * Load analytics from the backend and update the page state.
     */
    const fetchAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const response = await api.get('/api/analytics/summary');
            setAnalytics(response.data.data || null);
        } catch (error) {
            setAnalytics(null);
            if (error.response?.status === 401) throw error;
        } finally {
            setAnalyticsLoading(false);
        }
    }, []);

    /**
     * Load every review for the protected moderation controller.
     * Filtering is performed in the browser so switching tabs is instant.
     */
    const fetchReviews = useCallback(async () => {
        setReviewsLoading(true);
        try {
            const response = await api.get('/api/reviews/admin/all');
            setReviews(response.data.data || []);
            setReviewCounts(response.data.counts || { total: 0, pending: 0, approved: 0, rejected: 0 });
        } catch (error) {
            setReviews([]);
            if (error.response?.status === 401) throw error;
            window.alert(error.response?.data?.message || 'Impossible de charger les avis.');
        } finally {
            setReviewsLoading(false);
        }
    }, []);

    /**
     * Load admin users from the backend and update the page state.
     */
    const fetchAdminUsers = useCallback(async () => {
        const response = await api.get('/api/admin/users');
        setAdminUsers(response.data.data || []);
    }, []);

    /**
     * Load dashboard and store the result in component state.
     */
    const loadDashboard = useCallback(async (admin) => {
        const tasks = [fetchProperties(), fetchAnalytics(), fetchReviews()];
        if (admin?.role === 'owner') tasks.push(fetchAdminUsers());
        await Promise.all(tasks);
    }, [fetchAdminUsers, fetchAnalytics, fetchProperties, fetchReviews]);

    // SESSION RESTORE: verify the admin cookie before exposing protected dashboard content.
    useEffect(() => {
        /**
         * Encapsulate the check session behavior used by this file.
         */
        const checkSession = async () => {
            try {
                const response = await api.get('/api/admin/session');
                const admin = response.data.admin;
                setAuthenticated(true);
                setCurrentAdmin(admin);
                await loadDashboard(admin);
            } catch {
                forceLogin();
            } finally {
                setAuthChecking(false);
            }
        };

        checkSession();
    }, [loadDashboard]);

    // IMAGE PREVIEWS: create temporary browser URLs and revoke them when replacements change.
    useEffect(() => {
        const previewUrls = Object.fromEntries(
            Object.entries(editImageReplacements).map(([index, file]) => [
                index,
                URL.createObjectURL(file)
            ])
        );
        setEditPreviewUrls(previewUrls);

        return () => Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
    }, [editImageReplacements]);

    /**
     * Handle the login user action and keep interface state synchronized.
     */
    const handleLogin = async (event) => {
        event.preventDefault();
        setLoading(true);
        setAuthError('');

        try {
            const response = await api.post('/api/admin/login', loginData);
            const admin = response.data.admin;
            setAuthenticated(true);
            setCurrentAdmin(admin);
            setLoginData({ username: '', password: '' });
            await loadDashboard(admin);
        } catch (error) {
            setAuthError(error.response?.data?.message || 'Connexion impossible.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle the logout user action and keep interface state synchronized.
     */
    const handleLogout = async () => {
        try {
            await api.post('/api/admin/logout');
        } finally {
            forceLogin();
        }
    };

    /**
     * Handle the unauthorized user action and keep interface state synchronized.
     */
    const handleUnauthorized = (error, fallbackMessage) => {
        if (error.response?.status === 401) {
            forceLogin();
            setAuthError('Votre session a expiré. Connectez-vous de nouveau.');
            return;
        }
        window.alert(fallbackMessage || error.response?.data?.message || 'Une erreur est survenue.');
    };

    /**
     * Handle the submit user action and keep interface state synchronized.
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        const data = new FormData();

        Object.entries(formData).forEach(([key, value]) => {
            if (value !== '') data.append(key, value);
        });
        // Send custom fields as JSON because the rest of this form uses multipart/form-data for images.
        data.append('customFields', JSON.stringify(customFields.map(({ label, value, fieldType }) => ({ label, value, fieldType }))));
        Array.from(files || []).forEach((file) => data.append('images', file));

        try {
            await api.post('/api/properties', data);
            window.alert('✅ Bien publié avec succès.');
            setFormData(emptyPropertyForm);
            setCustomFields([]);
            setFiles([]);
            event.target.reset();
            await fetchProperties();
        } catch (error) {
            console.error('Error Detail:', error.response?.data || error.message);
            handleUnauthorized(error, `❌ ${error.response?.data?.message || 'Publication impossible.'}`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Open and prepare the edit modal interface.
     */
    const openEditModal = (property) => {
        setEditingProperty(property);
        setEditFormData({
            title: property.title || '',
            price: property.price ?? '',
            location: property.location || '',
            latitude: property.latitude ?? '',
            longitude: property.longitude ?? '',
            type: property.type || 'Vente',
            category: property.category || 'Autre',
            bedrooms: property.bedrooms ?? '',
            bathrooms: property.bathrooms ?? '',
            area: property.area ?? '',
            description: property.description || '',
            status: property.status || 'disponible',
            contactPhone: property.contactPhone || ''
        });
        setEditCustomFields((property.customFields || []).map((field) => makeCustomField(field)));
        setEditImageReplacements({});
    };

    /**
     * Close and reset the edit modal interface.
     */
    const closeEditModal = () => {
        if (editLoading) return;
        setEditingProperty(null);
        setEditFormData(emptyPropertyForm);
        setEditCustomFields([]);
        setEditImageReplacements({});
    };

    /**
     * Handle the edit image change user action and keep interface state synchronized.
     */
    const handleEditImageChange = (imageIndex, event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        setEditImageReplacements((current) => ({
            ...current,
            [imageIndex]: selectedFile
        }));
    };

    /**
     * Clear edit image replacement and release related temporary state.
     */
    const clearEditImageReplacement = (imageIndex) => {
        setEditImageReplacements((current) => {
            const next = { ...current };
            delete next[imageIndex];
            return next;
        });
    };

    /**
     * Handle the edit submit user action and keep interface state synchronized.
     */
    const handleEditSubmit = async (event) => {
        event.preventDefault();
        if (!editingProperty) return;

        setEditLoading(true);
        const data = new FormData();

        Object.entries(editFormData).forEach(([key, value]) => {
            data.append(key, value ?? '');
        });
        // An empty array intentionally removes all custom fields from this property.
        data.append('customFields', JSON.stringify(editCustomFields.map(({ label, value, fieldType }) => ({ label, value, fieldType }))));
        const replacementEntries = Object.entries(editImageReplacements)
            .map(([index, file]) => ({ index: Number(index), file }))
            .sort((a, b) => a.index - b.index);

        replacementEntries.forEach(({ file }) => data.append('images', file));
        if (replacementEntries.length > 0) {
            data.append('replacementIndexes', JSON.stringify(
                replacementEntries.map(({ index }) => index)
            ));
        }

        try {
            const response = await api.put(`/api/properties/${editingProperty._id}`, data);
            const updatedProperty = response.data.data;
            setProperties((current) => current.map((property) => (
                property._id === updatedProperty._id ? updatedProperty : property
            )));
            setEditingProperty(null);
            setEditFormData(emptyPropertyForm);
            setEditCustomFields([]);
            setEditImageReplacements({});
            window.alert('✅ Annonce modifiée avec succès.');
        } catch (error) {
            console.error('Edit error:', error.response?.data || error.message);
            handleUnauthorized(error, `❌ ${error.response?.data?.message || 'Modification impossible.'}`);
        } finally {
            setEditLoading(false);
        }
    };

    /**
     * Toggle status and persist the new state.
     */
    const toggleStatus = async (id, currentStatus, type) => {
        const newStatus = currentStatus === 'disponible'
            ? (type === 'Vente' ? 'vendu' : 'loué')
            : 'disponible';

        try {
            await api.put(`/api/properties/${id}`, { status: newStatus });
            setProperties((current) => current.map((property) => (
                property._id === id ? { ...property, status: newStatus } : property
            )));
        } catch (error) {
            handleUnauthorized(error, 'Erreur lors de la mise à jour du statut.');
        }
    };

    /**
     * Handle the delete user action and keep interface state synchronized.
     */
    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer définitivement ce bien ?')) return;

        try {
            await api.delete(`/api/properties/${id}`);
            setProperties((current) => current.filter((property) => property._id !== id));
            window.alert('Bien supprimé.');
        } catch (error) {
            handleUnauthorized(error, 'Erreur lors de la suppression.');
        }
    };

    /**
     * Permanently delete a user-submitted property from the moderation queue.
     *
     * This action is shown after an announcement has been refused. The existing
     * protected property DELETE endpoint also removes locally stored gallery files,
     * so the database record and uploaded images are cleaned together.
     */
    const handleDeleteSubmission = async (property) => {
        if (!property?._id) return;
        const title = property.title || 'cette annonce';
        if (!window.confirm(`Supprimer définitivement l’annonce refusée « ${title} » ? Cette action est irréversible.`)) return;

        setModerationLoadingId(property._id);
        try {
            await api.delete(`/api/properties/${property._id}`);
            // Remove the deleted card immediately without reloading the whole dashboard.
            setProperties((current) => current.filter((item) => item._id !== property._id));
            window.alert('Annonce utilisateur supprimée définitivement.');
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || "Impossible de supprimer l’annonce utilisateur.");
        } finally {
            setModerationLoadingId(null);
        }
    };

    /**
     * Approve or reject a user-submitted property and save an optional moderation note.
     */
    const moderateProperty = async (property, moderationStatus) => {
        const defaultMessage = moderationStatus === 'approved'
            ? 'Annonce acceptée et publiée.'
            : 'Annonce refusée. Ajoutez une raison pour aider le propriétaire.';
        const moderationNote = window.prompt('Message visible par le propriétaire :', defaultMessage);
        if (moderationNote === null) return;

        setModerationLoadingId(property._id);
        try {
            const response = await api.patch(`/api/properties/${property._id}/moderation`, {
                moderationStatus,
                moderationNote
            });
            const updated = response.data.data;
            setProperties((current) => current.map((item) => item._id === updated._id ? updated : item));
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || 'Décision impossible.');
        } finally {
            setModerationLoadingId(null);
        }
    };

    /**
     * Approve, reject or return one customer review to the pending queue.
     * Approving makes the review public and updates the property's average rating.
     */
    const moderateReview = async (review, moderationStatus) => {
        const labels = {
            approved: 'approuver et publier',
            rejected: 'masquer/refuser',
            pending: 'remettre en attente'
        };
        if (!window.confirm(`Voulez-vous ${labels[moderationStatus]} cet avis ?`)) return;

        setReviewModerationLoadingId(review._id);
        try {
            const response = await api.patch(`/api/reviews/admin/${review._id}`, { moderationStatus });
            const updatedReview = response.data.data;
            setReviews((current) => current.map((item) => item._id === updatedReview._id ? updatedReview : item));
            await Promise.all([fetchReviews(), fetchProperties()]);
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || "Impossible de modérer l'avis.");
        } finally {
            setReviewModerationLoadingId(null);
        }
    };

    /**
     * Permanently delete one customer review from the database.
     */
    const handleDeleteReview = async (review) => {
        if (!window.confirm(`Supprimer définitivement l'avis de ${review.name || 'Visiteur'} ?`)) return;

        setReviewModerationLoadingId(review._id);
        try {
            await api.delete(`/api/reviews/admin/${review._id}`);
            setReviews((current) => current.filter((item) => item._id !== review._id));
            await Promise.all([fetchReviews(), fetchProperties()]);
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || "Impossible de supprimer l'avis.");
        } finally {
            setReviewModerationLoadingId(null);
        }
    };

    /**
     * Handle the delete single image user action and keep interface state synchronized.
     */
    const handleDeleteSingleImage = async (imageIndex) => {
        if (!editingProperty) return;
        const currentImages = (editingProperty.images || []).filter(Boolean);
        if (currentImages.length <= 1) {
            window.alert("L'annonce doit conserver au moins une image.");
            return;
        }
        if (!window.confirm(`Supprimer définitivement l'image ${imageIndex + 1} ?`)) return;

        setEditLoading(true);
        try {
            const response = await api.delete(`/api/properties/${editingProperty._id}/images/${imageIndex}`);
            const updated = response.data.data;
            setEditingProperty(updated);
            setProperties((current) => current.map((item) => item._id === updated._id ? updated : item));
            setEditImageReplacements({});
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || "Suppression de l'image impossible.");
        } finally {
            setEditLoading(false);
        }
    };

    /**
     * Format duration for human-readable display.
     */
    const formatDuration = (seconds = 0) => {
        const value = Number(seconds || 0);
        if (value < 60) return `${value}s`;
        const minutes = Math.floor(value / 60);
        const remaining = value % 60;
        return `${minutes}m ${remaining}s`;
    };

    /**
     * Handle the create account user action and keep interface state synchronized.
     */
    const handleCreateAccount = async (event) => {
        event.preventDefault();
        setAccountLoading(true);

        try {
            const response = await api.post('/api/admin/users', accountForm);
            setAdminUsers((current) => [...current, response.data.data]);
            setAccountForm(emptyAccountForm);
            window.alert('✅ Compte administrateur créé.');
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || 'Création du compte impossible.');
        } finally {
            setAccountLoading(false);
        }
    };

    /**
     * Encapsulate the patch account behavior used by this file.
     */
    const patchAccount = async (user, changes) => {
        try {
            const response = await api.patch(`/api/admin/users/${user.id}`, changes);
            const updated = response.data.data;
            setAdminUsers((current) => current.map((item) => item.id === updated.id ? updated : item));
            if (updated.id === currentAdmin?.id) setCurrentAdmin(updated);
            return updated;
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || 'Modification du compte impossible.');
            return null;
        }
    };

    /**
     * Handle the toggle account user action and keep interface state synchronized.
     */
    const handleToggleAccount = async (user) => {
        await patchAccount(user, { active: !user.active });
    };

    /**
     * Handle the role change user action and keep interface state synchronized.
     */
    const handleRoleChange = async (user, role) => {
        await patchAccount(user, { role });
    };

    /**
     * Handle the reset password user action and keep interface state synchronized.
     */
    const handleResetPassword = async (user) => {
        const password = window.prompt(`Nouveau mot de passe pour ${user.username} (10 caractères minimum) :`);
        if (password === null) return;
        const updated = await patchAccount(user, { password });
        if (updated) window.alert('Mot de passe mis à jour. Les anciennes sessions de ce compte sont déconnectées.');
    };

    /**
     * Handle the delete account user action and keep interface state synchronized.
     */
    const handleDeleteAccount = async (user) => {
        if (!window.confirm(`Supprimer définitivement le compte ${user.username} ?`)) return;

        try {
            await api.delete(`/api/admin/users/${user.id}`);
            setAdminUsers((current) => current.filter((item) => item.id !== user.id));
        } catch (error) {
            handleUnauthorized(error, error.response?.data?.message || 'Suppression du compte impossible.');
        }
    };

    const pendingSubmissions = properties.filter((property) => property.source === 'user' && property.moderationStatus !== 'approved');
    const publishedProperties = properties.filter((property) => !property.moderationStatus || property.moderationStatus === 'approved');

    // REVIEW FILTER: old reviews without a status are treated as approved by the backend.
    const filteredReviews = reviewStatusFilter === 'all'
        ? reviews
        : reviews.filter((review) => review.moderationStatus === reviewStatusFilter);

    if (authChecking) {
        return <div className="admin-auth-loading">Vérification de la session...</div>;
    }

    if (!authenticated) {
        return (
            <div className="admin-login-page">
                <form className="admin-login-card" onSubmit={handleLogin}>
                    <div className="admin-login-icon">🔐</div>
                    <h1>Accès administrateur</h1>
                    <p>Connectez-vous pour gérer les annonces.</p>
                    <input
                        type="text"
                        autoComplete="username"
                        placeholder="Nom d'utilisateur"
                        value={loginData.username}
                        onChange={(event) => setLoginData({ ...loginData, username: event.target.value })}
                        required
                    />
                    <input
                        type="password"
                        autoComplete="current-password"
                        placeholder="Mot de passe"
                        value={loginData.password}
                        onChange={(event) => setLoginData({ ...loginData, password: event.target.value })}
                        required
                    />
                    {authError && <div className="admin-auth-error">{authError}</div>}
                    <button type="submit" className="add-btn" disabled={loading}>
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                    <a href="/" className="admin-home-link">← Retour au site</a>
                </form>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <header className="admin-header">
                <div>
                    <h1>Tableau de Bord - Capital Immobilière</h1>
                    <p>
                        Bienvenue, {currentAdmin?.displayName || currentAdmin?.username}
                        <span className="admin-role-badge">{currentAdmin?.role === 'owner' ? 'Propriétaire' : 'Éditeur'}</span>
                    </p>
                </div>
                <button type="button" className="logout-btn" onClick={handleLogout}>Déconnexion</button>
            </header>

            {/* EDIT HERE: visitor analytics dashboard. Raw IP addresses are not stored. */}
            <section className="analytics-section">
                <div className="section-heading admin-section-heading">
                    <div>
                        <h2>📊 Suivi des visiteurs</h2>
                        <p>Entrées, ouvertures des annonces et temps passé sur les fiches.</p>
                    </div>
                    <button type="button" className="analytics-refresh-btn" onClick={fetchAnalytics} disabled={analyticsLoading}>
                        {analyticsLoading ? 'Actualisation...' : 'Actualiser'}
                    </button>
                </div>
                <div className="analytics-cards">
                    <article><span>Visiteurs uniques</span><strong>{analytics?.uniqueVisitors ?? '—'}</strong></article>
                    <article><span>Sessions</span><strong>{analytics?.uniqueSessions ?? '—'}</strong></article>
                    <article><span>Biens ouverts</span><strong>{analytics?.propertyOpens ?? '—'}</strong></article>
                    <article><span>Temps moyen / bien</span><strong>{analytics ? formatDuration(analytics.averagePropertySeconds) : '—'}</strong></article>
                </div>
                <div className="analytics-details-grid">
                    <div className="analytics-panel">
                        <h3>Annonces les plus consultées</h3>
                        {(analytics?.topProperties || []).length ? (
                            <div className="analytics-list">
                                {analytics.topProperties.map((item) => (
                                    <div key={item._id}><span>{item.title}</span><strong>{item.opens} vues</strong></div>
                                ))}
                            </div>
                        ) : <p className="analytics-empty">Aucune consultation enregistrée.</p>}
                    </div>
                    <div className="analytics-panel">
                        <h3>Activité récente</h3>
                        {(analytics?.recentEvents || []).length ? (
                            <div className="analytics-event-list">
                                {analytics.recentEvents.slice(0, 10).map((event) => (
                                    <div key={event._id}>
                                        <span>{event.eventType === 'site_enter' ? 'Entrée sur le site' : event.eventType === 'property_open' ? 'Ouverture' : event.eventType === 'property_time' ? 'Temps passé' : 'Page vue'}</span>
                                        <b>{event.property?.title || event.path}</b>
                                        {event.eventType === 'property_time' && <small>{formatDuration(event.durationSeconds)}</small>}
                                    </div>
                                ))}
                            </div>
                        ) : <p className="analytics-empty">Aucune activité enregistrée.</p>}
                    </div>
                </div>
            </section>

            {/* EDIT HERE: moderation queue for properties submitted by site users. */}
            <section className="moderation-section">
                <div className="section-heading admin-section-heading">
                    <div>
                        <h2>🛡️ Annonces envoyées par les utilisateurs</h2>
                        <p>Acceptez ou refusez chaque annonce avant sa publication sur le site.</p>
                    </div>
                    <span className="moderation-count">{pendingSubmissions.length}</span>
                </div>
                {pendingSubmissions.length ? (
                    <div className="moderation-grid">
                        {pendingSubmissions.map((property) => (
                            <article key={property._id} className={`moderation-card ${property.moderationStatus}`}>
                                <img src={assetUrl(property.images?.[0])} alt={property.title} onError={(event) => { event.currentTarget.src = '/placeholder.svg'; }} />
                                <div className="moderation-card-body">
                                    <div className="moderation-meta">
                                        <span>{property.moderationStatus === 'rejected' ? 'Refusée' : 'En attente'}</span>
                                        <small>{property.submittedBy?.displayName || 'Utilisateur'} · {property.submittedBy?.email || ''}</small>
                                    </div>
                                    <h3>{property.title}</h3>
                                    <p>📍 {property.location} · {property.category || 'Autre'} · {Number(property.price).toLocaleString()} DT</p>
                                    <p className="moderation-description">{property.description}</p>
                                    {property.moderationNote && <p className="moderation-existing-note"><b>Message actuel :</b> {property.moderationNote}</p>}
                                    <div className="moderation-actions">
                                        <button type="button" className="approve-btn" onClick={() => moderateProperty(property, 'approved')} disabled={moderationLoadingId === property._id}>✓ Accepter et publier</button>
                                        <button type="button" className="reject-btn" onClick={() => moderateProperty(property, 'rejected')} disabled={moderationLoadingId === property._id}>✕ Refuser</button>
                                        <button type="button" className="btn-edit" onClick={() => openEditModal(property)} disabled={moderationLoadingId === property._id}>✏️ Vérifier / modifier</button>

                                        {/* REJECTED SUBMISSION DELETE: appears only after the administrator refuses the post. */}
                                        {property.moderationStatus === 'rejected' && (
                                            <button
                                                type="button"
                                                className="delete-submission-btn"
                                                onClick={() => handleDeleteSubmission(property)}
                                                disabled={moderationLoadingId === property._id}
                                            >
                                                🗑 Supprimer définitivement
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : <p className="moderation-empty">Aucune annonce utilisateur en attente.</p>}
            </section>

            {/* REVIEW CONTROLLER: approve, hide, reconsider or permanently delete customer reviews. */}
            <section className="reviews-moderation-section">
                <div className="section-heading admin-section-heading">
                    <div>
                        <h2>⭐ Contrôle des avis clients</h2>
                        <p>Les nouveaux avis restent invisibles jusqu'à votre approbation.</p>
                    </div>
                    <button type="button" className="analytics-refresh-btn" onClick={fetchReviews} disabled={reviewsLoading}>
                        {reviewsLoading ? 'Actualisation...' : 'Actualiser'}
                    </button>
                </div>

                {/* EDIT HERE: add or remove review status filters in this controller. */}
                <div className="review-moderation-toolbar" role="group" aria-label="Filtrer les avis">
                    {[
                        ['all', 'Tous', reviewCounts.total],
                        ['pending', 'En attente', reviewCounts.pending],
                        ['approved', 'Publiés', reviewCounts.approved],
                        ['rejected', 'Masqués', reviewCounts.rejected],
                    ].map(([status, label, count]) => (
                        <button
                            key={status}
                            type="button"
                            className={reviewStatusFilter === status ? 'active' : ''}
                            onClick={() => setReviewStatusFilter(status)}
                        >
                            {label} <span>{count || 0}</span>
                        </button>
                    ))}
                </div>

                {filteredReviews.length ? (
                    <div className="admin-review-list">
                        {filteredReviews.map((review) => {
                            const busy = reviewModerationLoadingId === review._id;
                            const propertyTitle = review.property?.title || 'Bien supprimé ou indisponible';
                            return (
                                <article key={review._id} className={`admin-review-card ${review.moderationStatus}`}>
                                    <div className="admin-review-card-top">
                                        <div>
                                            <span className={`review-status-badge ${review.moderationStatus}`}>
                                                {review.moderationStatus === 'approved' ? 'Publié' : review.moderationStatus === 'rejected' ? 'Masqué' : 'En attente'}
                                            </span>
                                            <h3>{review.name || 'Visiteur'}</h3>
                                        </div>
                                        <AdminStars rating={review.rating} />
                                    </div>

                                    <p className="admin-review-property">🏠 {propertyTitle}{review.property?.location ? ` · ${review.property.location}` : ''}</p>
                                    <p className="admin-review-comment">{review.comment || <em>Aucun commentaire écrit.</em>}</p>
                                    <div className="admin-review-date">
                                        Envoyé le {new Date(review.createdAt).toLocaleString('fr-FR')}
                                        {review.moderatedBy && <> · Décision par {review.moderatedBy.displayName || review.moderatedBy.username}</>}
                                    </div>

                                    <div className="admin-review-actions">
                                        {review.moderationStatus !== 'approved' && (
                                            <button type="button" className="approve-btn" onClick={() => moderateReview(review, 'approved')} disabled={busy}>✓ Approuver</button>
                                        )}
                                        {review.moderationStatus !== 'rejected' && (
                                            <button type="button" className="reject-btn" onClick={() => moderateReview(review, 'rejected')} disabled={busy}>✕ Masquer</button>
                                        )}
                                        {review.moderationStatus !== 'pending' && (
                                            <button type="button" className="review-pending-btn" onClick={() => moderateReview(review, 'pending')} disabled={busy}>↺ En attente</button>
                                        )}
                                        <button type="button" className="review-delete-btn" onClick={() => handleDeleteReview(review)} disabled={busy}>🗑 Supprimer</button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <p className="moderation-empty">Aucun avis dans cette catégorie.</p>
                )}
            </section>

            {currentAdmin?.role === 'owner' && (
                <section className="accounts-section">
                    <div className="section-heading">
                        <div>
                            <h2>👥 Comptes administrateurs</h2>
                            <p>Les propriétaires gèrent les comptes. Les éditeurs gèrent uniquement les biens.</p>
                        </div>
                        <span>{adminUsers.length} compte{adminUsers.length > 1 ? 's' : ''}</span>
                    </div>

                    <form className="account-form" onSubmit={handleCreateAccount}>
                        <input
                            type="text"
                            placeholder="Nom affiché"
                            value={accountForm.displayName}
                            onChange={(event) => setAccountForm({ ...accountForm, displayName: event.target.value })}
                            minLength="2"
                            maxLength="80"
                            required
                        />
                        <input
                            type="text"
                            placeholder="Nom d'utilisateur"
                            value={accountForm.username}
                            onChange={(event) => setAccountForm({ ...accountForm, username: event.target.value.toLowerCase() })}
                            pattern="[a-z0-9._-]{3,40}"
                            title="3 à 40 caractères: lettres minuscules, chiffres, point, tiret ou underscore"
                            autoComplete="off"
                            required
                        />
                        <input
                            type="password"
                            placeholder="Mot de passe (10 caractères minimum)"
                            value={accountForm.password}
                            onChange={(event) => setAccountForm({ ...accountForm, password: event.target.value })}
                            minLength="10"
                            autoComplete="new-password"
                            required
                        />
                        <select value={accountForm.role} onChange={(event) => setAccountForm({ ...accountForm, role: event.target.value })}>
                            <option value="editor">Éditeur</option>
                            <option value="owner">Propriétaire</option>
                        </select>
                        <button type="submit" className="account-create-btn" disabled={accountLoading}>
                            {accountLoading ? 'Création...' : 'Créer le compte'}
                        </button>
                    </form>

                    <div className="account-list">
                        {adminUsers.map((user) => {
                            const isSelf = user.id === currentAdmin?.id;
                            return (
                                <article key={user.id} className={`account-card ${user.active ? '' : 'inactive'}`}>
                                    <div className="account-main">
                                        <div className="account-avatar">{(user.displayName || user.username).slice(0, 1).toUpperCase()}</div>
                                        <div>
                                            <h3>{user.displayName} {isSelf && <small>(vous)</small>}</h3>
                                            <p>@{user.username}</p>
                                        </div>
                                    </div>
                                    <div className="account-controls">
                                        <select
                                            value={user.role}
                                            onChange={(event) => handleRoleChange(user, event.target.value)}
                                            disabled={isSelf}
                                            aria-label={`Rôle de ${user.username}`}
                                        >
                                            <option value="editor">Éditeur</option>
                                            <option value="owner">Propriétaire</option>
                                        </select>
                                        <span className={`account-state ${user.active ? 'active' : 'disabled'}`}>
                                            {user.active ? 'Actif' : 'Désactivé'}
                                        </span>
                                        <button type="button" className="account-secondary-btn" onClick={() => handleResetPassword(user)}>
                                            Mot de passe
                                        </button>
                                        <button
                                            type="button"
                                            className="account-secondary-btn"
                                            onClick={() => handleToggleAccount(user)}
                                            disabled={isSelf}
                                        >
                                            {user.active ? 'Désactiver' : 'Activer'}
                                        </button>
                                        <button
                                            type="button"
                                            className="account-danger-btn"
                                            onClick={() => handleDeleteAccount(user)}
                                            disabled={isSelf}
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="add-section">
                <h2>➕ Ajouter un nouveau bien</h2>
                <form onSubmit={handleSubmit} className="admin-form">
                    <input type="text" placeholder="Titre" value={formData.title} onChange={(event) => setFormData({ ...formData, title: event.target.value })} required />
                    <input type="number" min="0" placeholder="Prix (DT)" value={formData.price} onChange={(event) => setFormData({ ...formData, price: event.target.value })} required />
                    <input type="text" placeholder="Localisation" value={formData.location} onChange={(event) => setFormData({ ...formData, location: event.target.value })} required />
                    <input type="tel" placeholder="Téléphone (optionnel)" value={formData.contactPhone} onChange={(event) => setFormData({ ...formData, contactPhone: event.target.value })} />

                    <select value={formData.type} onChange={(event) => setFormData({ ...formData, type: event.target.value })}>
                        <option value="Vente">Vente (بيع)</option>
                        <option value="Location">Location (كراء)</option>
                    </select>
                    {/* EDIT HERE: property categories used by the public advanced filter. */}
                    <select value={formData.category} onChange={(event) => setFormData({ ...formData, category: event.target.value })}>
                        {['Appartement', 'Villa', 'Maison', 'Terrain', 'Bureau', 'Local commercial', 'Autre'].map((category) => <option key={category} value={category}>{category}</option>)}
                    </select>
                    <input type="number" min="0" max="100" placeholder="Chambres" value={formData.bedrooms} onChange={(event) => setFormData({ ...formData, bedrooms: event.target.value })} />
                    <input type="number" min="0" max="100" placeholder="Salles de bain" value={formData.bathrooms} onChange={(event) => setFormData({ ...formData, bathrooms: event.target.value })} />
                    <input type="number" min="0" placeholder="Surface (m²)" value={formData.area} onChange={(event) => setFormData({ ...formData, area: event.target.value })} />

                    {/* PROPERTY MAP: select the position that visitors will see on this listing's details page. */}
                    <PropertyMapPicker
                        latitude={formData.latitude}
                        longitude={formData.longitude}
                        required
                        onChange={({ latitude, longitude }) => setFormData((current) => ({ ...current, latitude, longitude }))}
                    />

                    {/* CUSTOM FIELD BUILDER: add/remove any extra facts required by this individual listing. */}
                    <CustomFieldsEditor fields={customFields} onChange={setCustomFields} />

                    <label>Sélectionnez les images (10 maximum, 8 Mo chacune) :</label>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={(event) => setFiles(event.target.files)} required />

                    <textarea placeholder="Description..." value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} required />

                    <button type="submit" className="add-btn" disabled={loading}>
                        {loading ? 'Chargement...' : "Publier l'annonce"}
                    </button>
                </form>
            </section>

            <section className="manage-section">
                <h2>📂 Gérer les biens publiés ({publishedProperties.length})</h2>
                <div className="admin-grid">
                    {publishedProperties.map((property) => (
                        <div key={property._id} className={`admin-card ${property.status}`}>
                            <div className="admin-card-img">
                                <img
                                    src={assetUrl(property.images?.[0] || property.image)}
                                    alt={property.title}
                                    onError={(event) => { event.currentTarget.src = '/placeholder.svg'; }}
                                />
                                {property.status !== 'disponible' && <span className="status-label">{property.status}</span>}
                            </div>
                            <div className="admin-card-info">
                                <h3>{property.title}</h3>
                                <p><strong>{Number(property.price).toLocaleString()} DT</strong></p>
                                <p className="loc-text">📍 {property.location}</p>
                                <p className="admin-card-meta">{property.category || 'Autre'} · {property.area ? `${property.area} m²` : 'Surface non indiquée'} · {property.source === 'user' ? 'Annonce utilisateur' : 'Annonce agence'}</p>

                                <div className="admin-actions">
                                    <button
                                        type="button"
                                        className="btn-edit"
                                        onClick={() => openEditModal(property)}
                                    >
                                        ✏️ Modifier
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-status ${property.status}`}
                                        onClick={() => toggleStatus(property._id, property.status, property.type)}
                                    >
                                        {property.status === 'disponible' ? 'Marquer Vendu/Loué' : 'Rendre Disponible'}
                                    </button>
                                    <button type="button" className="btn-delete" onClick={() => handleDelete(property._id)}>🗑️</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {editingProperty && (
                <div className="edit-modal-backdrop" onMouseDown={closeEditModal}>
                    <section
                        className="edit-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="edit-property-title"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className="edit-modal-header">
                            <div>
                                <span className="edit-modal-kicker">Modifier l'annonce</span>
                                <h2 id="edit-property-title">{editingProperty.title}</h2>
                            </div>
                            <button
                                type="button"
                                className="edit-close-btn"
                                onClick={closeEditModal}
                                disabled={editLoading}
                                aria-label="Fermer"
                            >
                                ✕
                            </button>
                        </div>

                        <form className="admin-form edit-property-form" onSubmit={handleEditSubmit}>
                            <div className="form-field">
                                <label htmlFor="edit-title">Titre</label>
                                <input
                                    id="edit-title"
                                    type="text"
                                    value={editFormData.title}
                                    onChange={(event) => setEditFormData({ ...editFormData, title: event.target.value })}
                                    maxLength="150"
                                    required
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-price">Prix (DT)</label>
                                <input
                                    id="edit-price"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={editFormData.price}
                                    onChange={(event) => setEditFormData({ ...editFormData, price: event.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-location">Localisation</label>
                                <input
                                    id="edit-location"
                                    type="text"
                                    value={editFormData.location}
                                    onChange={(event) => setEditFormData({ ...editFormData, location: event.target.value })}
                                    maxLength="200"
                                    required
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-phone">Téléphone</label>
                                <input
                                    id="edit-phone"
                                    type="tel"
                                    value={editFormData.contactPhone}
                                    onChange={(event) => setEditFormData({ ...editFormData, contactPhone: event.target.value })}
                                    maxLength="40"
                                    placeholder="Optionnel"
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-type">Type</label>
                                <select
                                    id="edit-type"
                                    value={editFormData.type}
                                    onChange={(event) => setEditFormData({ ...editFormData, type: event.target.value })}
                                >
                                    <option value="Vente">Vente (بيع)</option>
                                    <option value="Location">Location (كراء)</option>
                                </select>
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-category">Catégorie</label>
                                <select id="edit-category" value={editFormData.category} onChange={(event) => setEditFormData({ ...editFormData, category: event.target.value })}>
                                    {['Appartement', 'Villa', 'Maison', 'Terrain', 'Bureau', 'Local commercial', 'Autre'].map((category) => <option key={category} value={category}>{category}</option>)}
                                </select>
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-bedrooms">Chambres</label>
                                <input id="edit-bedrooms" type="number" min="0" max="100" value={editFormData.bedrooms} onChange={(event) => setEditFormData({ ...editFormData, bedrooms: event.target.value })} />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-bathrooms">Salles de bain</label>
                                <input id="edit-bathrooms" type="number" min="0" max="100" value={editFormData.bathrooms} onChange={(event) => setEditFormData({ ...editFormData, bathrooms: event.target.value })} />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-area">Surface (m²)</label>
                                <input id="edit-area" type="number" min="0" value={editFormData.area} onChange={(event) => setEditFormData({ ...editFormData, area: event.target.value })} />
                            </div>

                            <div className="form-field">
                                <label htmlFor="edit-status">Statut</label>
                                <select
                                    id="edit-status"
                                    value={editFormData.status}
                                    onChange={(event) => setEditFormData({ ...editFormData, status: event.target.value })}
                                >
                                    <option value="disponible">Disponible</option>
                                    <option value="vendu">Vendu</option>
                                    <option value="loué">Loué</option>
                                </select>
                            </div>

                            {/* PROPERTY MAP: old listings without a point must receive one before the edit can be saved. */}
                            <PropertyMapPicker
                                latitude={editFormData.latitude}
                                longitude={editFormData.longitude}
                                required
                                onChange={({ latitude, longitude }) => setEditFormData((current) => ({ ...current, latitude, longitude }))}
                            />

                            {/* CUSTOM FIELD BUILDER: existing rows are editable and can be removed before saving. */}
                            <CustomFieldsEditor fields={editCustomFields} onChange={setEditCustomFields} />

                            <div className="form-field form-field-full">
                                <label htmlFor="edit-description">Description</label>
                                <textarea
                                    id="edit-description"
                                    value={editFormData.description}
                                    onChange={(event) => setEditFormData({ ...editFormData, description: event.target.value })}
                                    maxLength="5000"
                                    required
                                />
                            </div>

                            <div className="edit-images-section form-field-full">
                                <div className="edit-images-heading">
                                    <div>
                                        <h3>Images de l'annonce</h3>
                                        <p>Remplacez uniquement les images de votre choix. Toutes les autres resteront inchangées.</p>
                                    </div>
                                    <span>{(editingProperty.images?.length ? editingProperty.images : [editingProperty.image]).filter(Boolean).length} image(s)</span>
                                </div>

                                <div className="edit-image-grid edit-image-replacement-grid">
                                    {(editingProperty.images?.length ? editingProperty.images : [editingProperty.image]).filter(Boolean).map((image, index) => {
                                        const hasReplacement = Boolean(editImageReplacements[index]);
                                        const previewUrl = editPreviewUrls[index];

                                        return (
                                            <article className={`edit-image-card${hasReplacement ? ' has-replacement' : ''}`} key={`${image}-${index}`}>
                                                <div className="edit-image-frame">
                                                    <img
                                                        src={previewUrl || assetUrl(image)}
                                                        alt={hasReplacement ? `Nouvel aperçu de l'image ${index + 1}` : `Image actuelle ${index + 1}`}
                                                        className={`edit-image-preview${hasReplacement ? ' new' : ''}`}
                                                        onError={(event) => { event.currentTarget.src = '/placeholder.svg'; }}
                                                    />
                                                    <span className="edit-image-number">Image {index + 1}</span>
                                                    {hasReplacement && <span className="edit-image-changed">À remplacer</span>}
                                                </div>

                                                <label className="replace-image-btn" htmlFor={`edit-image-${index}`}>
                                                    {hasReplacement ? 'Choisir une autre image' : 'Remplacer cette image'}
                                                </label>
                                                <input
                                                    key={`${index}-${hasReplacement ? 'selected' : 'empty'}`}
                                                    id={`edit-image-${index}`}
                                                    className="replace-image-input"
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                                    onChange={(event) => handleEditImageChange(index, event)}
                                                />

                                                {/* EDIT HERE: delete one image without deleting the whole property. */}
                                                <button
                                                    type="button"
                                                    className="delete-single-image-btn"
                                                    onClick={() => handleDeleteSingleImage(index)}
                                                    disabled={editLoading || (editingProperty.images || []).filter(Boolean).length <= 1}
                                                >
                                                    🗑️ Supprimer cette image
                                                </button>

                                                {hasReplacement && (
                                                    <button
                                                        type="button"
                                                        className="cancel-image-replacement-btn"
                                                        onClick={() => clearEditImageReplacement(index)}
                                                    >
                                                        Garder l'image actuelle
                                                    </button>
                                                )}
                                            </article>
                                        );
                                    })}
                                </div>

                                <p className="edit-image-note">
                                    Vous pouvez remplacer une ou plusieurs images séparément. Par exemple, remplacer l'image 4 conservera les images 1–3 et 5–10.
                                </p>
                            </div>

                            <div className="edit-modal-actions form-field-full">
                                <button type="button" className="edit-cancel-btn" onClick={closeEditModal} disabled={editLoading}>
                                    Annuler
                                </button>
                                <button type="submit" className="add-btn edit-save-btn" disabled={editLoading}>
                                    {editLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
